import React from "react";

export type ViewerDisplayMode = "ONLY_2D" | "ONLY_3D" | "BOTH_2D_3D";

const iconStyle: React.CSSProperties = {
  display: "block",
  width: 15,
  height: 15,
  flex: "0 0 auto",
};

const MapIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={iconStyle}>
    <path
      d="M4 6.5 9 4l6 2.5L20 4v13.5l-5 2.5-6-2.5L4 20V6.5Z"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
    <path d="M9 4v13.5M15 6.5V20" fill="none" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const CubeIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={iconStyle}>
    <path
      d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z"
      fill="none"
      stroke="currentColor"
      strokeLinejoin="round"
      strokeWidth="2"
    />
    <path
      d="M4.4 7.7 12 12l7.6-4.3M12 12v8.5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const SplitViewIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={iconStyle}>
    <path d="M4 5h6v14H4V5ZM14 5h6v14h-6V5Z" fill="currentColor" />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={iconStyle}>
    <path
      d="M7 10V8a5 5 0 0 1 10 0v2M6 10h12v10H6V10Z"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const UnlockIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={iconStyle}>
    <path
      d="M7 10V8a5 5 0 0 1 9.6-1.9M6 10h12v10H6V10Z"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={iconStyle}>
    <path
      d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
    <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={iconStyle}>
    <path
      d="m3 3 18 18M10.6 10.6A3 3 0 0 0 12 15a3 3 0 0 0 2.4-4.8M7.3 7.5C4.3 9.2 2.5 12 2.5 12s3.5 6 9.5 6c1.7 0 3.2-.5 4.5-1.2M19.1 14.1c1.5-1.1 2.4-2.1 2.4-2.1s-3.5-6-9.5-6c-.8 0-1.6.1-2.3.3"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const MODE_OPTIONS: Array<{ value: ViewerDisplayMode; label: React.ReactNode }> = [
  {
    value: "ONLY_2D",
    label: (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <MapIcon />
        2D
      </span>
    ),
  },
  {
    value: "ONLY_3D",
    label: (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <CubeIcon />
        3D
      </span>
    ),
  },
  {
    value: "BOTH_2D_3D",
    label: (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <SplitViewIcon />
        2D + 3D
      </span>
    ),
  },
];

const modeButtonBaseStyle: React.CSSProperties = {
  height: 30,
  border: 0,
  borderRadius: 6,
  padding: "0 10px",
  background: "transparent",
  color: "#495057",
  cursor: "pointer",
  font: "inherit",
  fontSize: 12,
  lineHeight: 1,
};

interface ViewerModeButtonBarProps {
  displayMode: ViewerDisplayMode;
  onDisplayModeChange: (displayMode: ViewerDisplayMode) => void;
  syncViewports: boolean;
  onSyncViewportsChange: (syncViewports: boolean) => void;
  showIfcCoveringToggle?: boolean;
  hideIfcCovering?: boolean;
  onHideIfcCoveringChange?: (hideIfcCovering: boolean) => void;
}

export const ViewerModeButtonBar: React.FC<ViewerModeButtonBarProps> = ({
  displayMode,
  onDisplayModeChange,
  syncViewports,
  onSyncViewportsChange,
  showIfcCoveringToggle = false,
  hideIfcCovering = true,
  onHideIfcCoveringChange,
}) => {
  return (
    <div
      style={{
        position: "absolute",
        top: 12,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 30,
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <div
        style={{
          boxShadow: "0 2px 10px rgba(15, 23, 42, 0.16)",
          borderRadius: 8,
          background: "white",
          display: "flex",
          alignItems: "center",
          gap: 2,
          padding: 3,
        }}
      >
        {MODE_OPTIONS.map((option) => {
          const active = option.value === displayMode;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={active}
              onClick={() => onDisplayModeChange(option.value)}
              style={{
                ...modeButtonBaseStyle,
                background: active ? "#f1f3f5" : "transparent",
                color: active ? "#111827" : "#495057",
                boxShadow: active ? "0 1px 3px rgba(15, 23, 42, 0.14)" : undefined,
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {displayMode === "BOTH_2D_3D" && (
        <button
          type="button"
          title={syncViewports ? "Unlock viewports" : "Lock viewports"}
          aria-label={syncViewports ? "Unlock viewports" : "Lock viewports"}
          aria-pressed={syncViewports}
          style={{
            width: 36,
            height: 36,
            border: syncViewports ? "1px solid #228be6" : "1px solid #dee2e6",
            borderRadius: 6,
            background: syncViewports ? "#228be6" : "white",
            color: syncViewports ? "white" : "#495057",
            boxShadow: "0 2px 10px rgba(15, 23, 42, 0.16)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
          onClick={() => onSyncViewportsChange(!syncViewports)}
        >
          {syncViewports ? <LockIcon /> : <UnlockIcon />}
        </button>
      )}

      {showIfcCoveringToggle && (
        <button
          type="button"
          title={hideIfcCovering ? "Visa undertak (IfcCovering)" : "Dölj undertak (IfcCovering)"}
          aria-label={hideIfcCovering ? "Visa undertak" : "Dölj undertak"}
          aria-pressed={hideIfcCovering}
          style={{
            height: 36,
            border: hideIfcCovering ? "1px solid #228be6" : "1px solid #dee2e6",
            borderRadius: 6,
            background: hideIfcCovering ? "#228be6" : "white",
            color: hideIfcCovering ? "white" : "#495057",
            boxShadow: "0 2px 10px rgba(15, 23, 42, 0.16)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            padding: "0 10px",
            cursor: "pointer",
            font: "inherit",
            fontSize: 12,
            lineHeight: 1,
            whiteSpace: "nowrap",
          }}
          onClick={() => onHideIfcCoveringChange?.(!hideIfcCovering)}
        >
          {hideIfcCovering ? <EyeOffIcon /> : <EyeIcon />}
          Undertak
        </button>
      )}
    </div>
  );
};
